'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { ElasticArticleItem } from '@//app/api/articles';
import { useLegacyTrackNewsItemExposure } from '@//data/tracking/useLegacyTrackNewsItemExposure';

interface ArticlePreviewTrackerProps {
  article: ElasticArticleItem;
  threshold?: number;
  trackOnce?: boolean;
}

export const ArticlePreviewTracker = ({
  article,
  threshold = 0.5,
  trackOnce = true,
}: ArticlePreviewTrackerProps) => {
  const { trackLegacyNewsItemExposure } = useLegacyTrackNewsItemExposure();
  const hasTracked = useRef(false);
  const trackerRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry.isIntersecting) {
        if (trackOnce && hasTracked.current) {
          return;
        }

        trackLegacyNewsItemExposure(article.id, article.title);
        hasTracked.current = true;
      }
    },
    [trackLegacyNewsItemExposure, article, trackOnce],
  );

  useEffect(() => {
    const trackerElement = trackerRef.current;
    if (!trackerElement) return;

    const nextSibling = trackerElement.nextElementSibling;
    if (!nextSibling) {
      console.warn('ArticlePreviewTracker: No next sibling element found to observe');
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin: '0px',
    });

    observer.observe(nextSibling);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, threshold]);

  return <div ref={trackerRef} style={{ display: 'none' }} />;
};
